interface ParsedContent {
  content: string;
}

async function fetchWithRetry(url: string, maxRetries = 3): Promise<string> {
  const proxyUrls = [
    `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`,
    `https://corsproxy.io/?${encodeURIComponent(url)}`,
    `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(url)}`
  ];

  let lastError;
  for (let retry = 0; retry < maxRetries; retry++) {
    for (const proxyUrl of proxyUrls) {
      try {
        console.log('Trying proxy:', proxyUrl);
        
        const response = await fetch(proxyUrl, {
          headers: {
            'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36'
          },
          signal: AbortSignal.timeout(10000)
        });
        
        if (!response.ok) {
          console.warn(`Proxy ${proxyUrl} returned status ${response.status}`);
          continue;
        }
        
        const html = await response.text();
        
        if (html.length > 500 && 
            (html.includes('</html>') || html.includes('</body>'))) {
          console.log('Successfully fetched content with length:', html.length);
          return html;
        }
        
        console.warn(`Proxy ${proxyUrl} returned invalid content length: ${html.length}`);
      } catch (error) {
        lastError = error;
        console.warn(`Proxy ${proxyUrl} failed:`, error);
        continue;
      }
    }
    
    await new Promise(resolve => setTimeout(resolve, 1000 * (retry + 1)));
  }

  throw lastError || new Error('Failed to fetch content after all retries');
}

function validateText(text: string | null | undefined): string {
  if (!text || typeof text !== 'string') {
    return '';
  }
  return text.trim();
}

function isValidContent(text: string): boolean {
  if (!text || text.length < 5) return false;
  
  if (!/\p{L}/u.test(text)) return false;
  
  const skipWords = [
    'menu', 'navigation', 'skip', 'search', 'cookie', 'privacy',
    'login', 'sign in', 'register', 'subscribe', 'newsletter'
  ];
  
  const textLower = text.toLowerCase();
  return !skipWords.some(word => textLower === word);
}

function findMainContent(doc: Document): Element {
  const contentSelectors = [
    'main',
    'article',
    '[role="main"]',
    '#main-content',
    '#content',
    '.content',
    '.main-content',
    '.post-content',
    '.article-content',
    '.entry-content'
  ];

  for (const selector of contentSelectors) {
    const element = doc.querySelector(selector);
    if (element && element.textContent && element.textContent.trim().length > 200) {
      return element;
    }
  }

  let bestElement = doc.body;
  let maxTextLength = 0;

  const allElements = doc.body.querySelectorAll('*');
  allElements.forEach(el => {
    if (el.matches('header, footer, nav, aside, style, script, meta, link, .sidebar, .menu, .navigation')) {
      return;
    }

    const textLength = el.textContent ? el.textContent.trim().length : 0;
    
    if (textLength > maxTextLength) {
      maxTextLength = textLength;
      bestElement = el as HTMLElement;
    }
  });

  return bestElement;
}

function processTextContent(element: Element): string {
  let contentHtml = '';

  // Process all elements in document order to preserve structure
  const allElements = element.querySelectorAll('h1, h2, h3, h4, h5, h6, p, div, section, article, ul, ol');
  
  // Create a map to track processed elements to avoid duplicates
  const processedElements = new Set<Element>();
  
  // Get elements in document order
  const elementsInOrder = Array.from(allElements).sort((a, b) => {
    const position = a.compareDocumentPosition(b);
    if (position & Node.DOCUMENT_POSITION_FOLLOWING) {
      return -1; // a comes before b
    } else if (position & Node.DOCUMENT_POSITION_PRECEDING) {
      return 1; // a comes after b
    }
    return 0; // same element
  });

  elementsInOrder.forEach(el => {
    if (processedElements.has(el)) return;
    
    const tagName = el.tagName.toLowerCase();
    
    // Process headers
    if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) {
      const text = validateText(el.textContent);
      if (isValidContent(text)) {
        contentHtml += `<${tagName}>${text}</${tagName}>\n`;
        processedElements.add(el);
      }
    }
    // Process paragraphs and divs (only leaf elements)
    else if (['p', 'div', 'section', 'article'].includes(tagName)) {
      // Skip if this element contains other block elements
      if (el.querySelector('p, div, section, article, h1, h2, h3, h4, h5, h6')) {
        return;
      }
      
      const text = validateText(el.textContent);
      if (isValidContent(text)) {
        contentHtml += `<p>${text}</p>\n`;
        processedElements.add(el);
      }
    }
    // Process lists
    else if (['ul', 'ol'].includes(tagName)) {
      let listHtml = `<${tagName}>`;
      
      el.querySelectorAll('li').forEach(li => {
        const text = validateText(li.textContent);
        if (isValidContent(text)) {
          listHtml += `<li>${text}</li>`;
        }
      });
      
      listHtml += `</${tagName}>`;
      
      if (listHtml !== `<${tagName}></${tagName}>`) {
        contentHtml += listHtml + '\n';
        processedElements.add(el);
      }
    }
  });

  return contentHtml;
}

export async function parseUrlForEditor(url: string): Promise<ParsedContent> {
  try {
    console.log('Fetching URL for editor:', url);
    const html = await fetchWithRetry(url);
    
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Remove unwanted elements
    const unwantedSelectors = [
      'script', 'style', 'noscript', 'iframe', 'meta', 'link', 'svg', 
      '.cookie', '.popup', '.modal', 'form', 'button', '[role="complementary"]', 
      '[role="banner"]', '[role="contentinfo"]', '.ad', '.advertisement', 
      '.social-share', '.share-buttons', '.related-posts', '.comments', '#comments'
    ];
    
    unwantedSelectors.forEach(selector => {
      doc.querySelectorAll(selector).forEach(el => el.remove());
    });

    const mainContent = findMainContent(doc);
    console.log('Found main content element:', mainContent.tagName);

    let contentHtml = processTextContent(mainContent);
    console.log('Processed content HTML (first 200 chars):', contentHtml.substring(0, 200));

    contentHtml = contentHtml
      .replace(/\n{3,}/g, '\n\n')
      .replace(/\s+/g, ' ')
      .trim();

    console.log('Parsed content length:', contentHtml.length);
    console.log('Final content (first 200 chars):', contentHtml.substring(0, 200));
    
    if (!contentHtml.trim()) {
      throw new Error('No valid content found');
    }

    return { content: contentHtml };
  } catch (error) {
    console.error('Error parsing URL for editor:', error);
    throw error;
  }
}